import type { CSSProperties, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAudioTrigger } from '../../controllers/audioController/hooks';
import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { useGameRedirectConnected } from '../../hooks/useGameRedirectConnected';
import { useLobbyURLConnected } from '../../hooks/useLobbyURLConnected';
import { useShouldStayConnected } from '../../hooks/useShouldStayConnected';
import { CoreLobbyMessageController, tableInfoMessage } from '../../messages/CoreLobbyMessageController';
import { useUpdateBalanceMessageCallbackConnected } from '../../messages/messages/updateBalance/UpdateBalanceMessageSenderConnected';
import { useCoreDispatch } from '../../store/CoreStore';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { gameStateActions } from '../../store/slices/game/slice';
import { useHeaderSlice } from '../../store/slices/header/selectors';
import { headerActions } from '../../store/slices/header/slice';
import { popupActions } from '../../store/slices/popup/slice';
import { useIsLargerThanWide } from '../../styles/hooks/media';
import { useModifiers } from '../../styles/hooks/useModifiers';
import { isCurrentBrowserIOS } from '../../utils/browsers';
import { debounce } from '../../utils/debounce';
import { URLUtilities } from '../../utils/url';
import { IconButton, Icons } from '../controls/iconButton/IconButton';
import { NavPages } from '../nav/navPages/NavPagesConnected';
import { PopupType } from '../popup/types';

import { detectPortrait, generateLobbyUrl } from './utils';

import styles from './MiniLobbyConnected.module.css';

export const MiniLobbyBodyConnected = (): ReactElement | null => {
  /** URL configuration starts here **/
  const session = useConfigSlice.session();
  const env = useConfigSlice.env();
  const tableConfig = useConfigSlice.tableConfig();
  const casinoConfig = useConfigSlice.casinoConfig();
  const lobbyVersion = casinoConfig?.lobbyVersion;
  const lobbyFolder = useLobbyURLConnected({ folderOnly: true });

  const JSESSIONID = session?.JSESSIONID ?? '';
  const casino_id = session?.casinoId ?? '';
  const web_server = session?.webServer ?? '';
  const config_url = session?.configUrl ?? '';
  const socket_server = session?.socketServer ?? '';
  const actual_web_server = session?.actualWebServer ?? '';
  const lang = session?.lang ?? '';
  const stats_collector_uuid = session?.statsCollectorUuid ?? '';
  const socket_port = session?.socketPort ?? '';
  const uiversion = session?.uiversion ?? '';
  const meta_server = session?.metaServer ?? '';
  const common_components_theme = session?.commonComponentsTheme ?? '';

  const operator_theme = tableConfig?.operatorTheme ?? '';
  const playerUI = env?.playerUI ?? '';
  const uiAddress = URLUtilities.combine(playerUI, `${lobbyFolder}/`).toString();
  const lobby_loading = useIsLargerThanWide() ? '' : 'mobile';

  const searchParams = useMemo(() => {
    return new URLSearchParams({
      JSESSIONID,
      actual_web_server,
      casino_id,
      config_url,
      game_mode: 'html5_desktop',
      gametype: 'all',
      lang,
      meta_server,
      nocache: '1',
      operator_theme,
      socket_port,
      socket_server,
      stats_collector_uuid,
      swf_lobby_path: '/member/games/lobby.swf',
      timeStamp: String(Date.now()),
      token: JSESSIONID,
      uiAddress,
      uiversion,
      web_server,
      lobby_loading,
      common_components_theme,
    });
  }, [
    JSESSIONID,
    actual_web_server,
    casino_id,
    config_url,
    lang,
    meta_server,
    operator_theme,
    socket_port,
    socket_server,
    stats_collector_uuid,
    uiAddress,
    uiversion,
    web_server,
    lobby_loading,
    common_components_theme,
  ]);

  const LOBBY_URL = generateLobbyUrl(uiAddress, searchParams);
  /** URL configuration ends here **/

  const iframeWrapperRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false);
  const [isLobbyFullscreen, setIsLobbyFullscreen] = useState<boolean>(false);
  const [iframeDocument, setIframeDocument] = useState<Document | null>(null);

  const [windowSize, setWindowSize] = useState<{ width: number; height: number }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const windowAspectRatio = useMemo(() => windowSize.height / windowSize.width, [windowSize]);
  // lobby scale is detected by dividing preloader element's width by 1920 for desktop devices (https://gitlab.gametechlabs.net/pp-live/appmobile/-/blob/master/src/assets/preloaders/src/preloader.js#L104) @ 2023-12-13
  const defaultAspectRatio = 0.5625; // 16:9 aspect ratio (divide 9 by 16)
  // Adjust iframe wrapper div width to reduce black gaps for screens beyond 16:9 aspect ratio
  const iframeWrapperWidth = useMemo(() => windowAspectRatio * defaultAspectRatio * 100, [windowAspectRatio]);
  // Adjust iframe width to reduce black gaps for screens beyond 16:9 aspect ratio
  const iframeWidth = useMemo(() => (windowAspectRatio / defaultAspectRatio) * 100, [windowAspectRatio]);

  const isWideScreen = useIsLargerThanWide();
  const fullWidth = !isWideScreen || isLobbyFullscreen ? 100 : null;

  // Debounced resize event handler
  useEffect(() => {
    const handleResize = debounce(() => {
      const { innerWidth, innerHeight } = window;
      setWindowSize({ width: innerWidth, height: innerHeight });
    }, 50);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Detect iframe content and gracefully handle CORS errors preventing hitting error boundary
  useEffect(() => {
    try {
      if (iframeRef.current) {
        const document = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
        setIframeDocument(document !== undefined ? document : null);
      }
    } catch (error) {
      console.error('Error accessing iframe content:', error);
      setIframeDocument(null);
    }
  }, [isIframeLoaded]);

  const updateBalance = useUpdateBalanceMessageCallbackConnected();
  // fixme: This does not seem to update balance in mini lobby. Better to move to handleMessageFromIframe() if should be done once on iframe load
  useEffect(() => {
    if (!isIframeLoaded) return;
    updateBalance();
  }, [isIframeLoaded, updateBalance]);

  const dispatch = useCoreDispatch();
  const shouldStayAtTable = useShouldStayConnected();
  // 💩 Italy Compliance: prevent selecting other tables while in a round with accepted bets, LCE-58662
  useEffect(() => {
    if (iframeDocument) {
      const lobbyTabContent = iframeDocument.querySelector('.tabContent');

      if (lobbyTabContent) {
        const handleTabContentClick = (event: Event): void => {
          if (shouldStayAtTable) {
            event.preventDefault();
            event.stopPropagation();
            dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress));
          }
        };

        lobbyTabContent.addEventListener('click', handleTabContentClick, true);
        lobbyTabContent.addEventListener('touchend', handleTabContentClick, true);

        return () => {
          lobbyTabContent.removeEventListener('click', handleTabContentClick, true);
          lobbyTabContent.removeEventListener('touchend', handleTabContentClick, true);
        };
      }
    }
  }, [dispatch, iframeDocument, shouldStayAtTable]);

  const tableId = useConfigSlice.session()?.tableId;
  const gameRedirect = useGameRedirectConnected();
  const audioTrigger = useAudioTrigger();

  // Handle message events in iframe
  useEffect(() => {
    const handleMessageFromIframe = (event: MessageEvent): void => {
      const message = event.data;

      if (!tableId) return;

      switch (message.type) {
        // Handle initial messages on iframe load. All three require post response with tableId to display Current Table badge.
        case 'notifyfsloaded_pp': // First message
        case 'notifyoperatorId': // Second message
          CoreLobbyMessageController.post(
            tableInfoMessage({
              type: 'tableInfo',
              tableId,
            })
          );

          break;

        case 'pp_get_tableId': // Third message
          CoreLobbyMessageController.post(
            tableInfoMessage({
              type: 'tableInfo',
              tableId,
            })
          );

          setIsIframeLoaded(true);
          break;

        // Close lobby mobile
        case 'closeLobbyMenu':
          dispatch(headerActions.setMiniLobbyOpen(false));
          dispatch(headerActions.setOpenedModal(null));
          dispatch(headerActions.setNavRoute([]));
          audioTrigger.play({ name: 'menuClick' });
          break;

        // Close lobby desktop
        case 'hideLobbyIframe':
          if (isLobbyFullscreen) {
            setIsLobbyFullscreen(false);
          }
          dispatch(headerActions.setNavRoute([]));
          dispatch(headerActions.setMiniLobbyOpen(false));
          audioTrigger.play({ name: 'menuClick' });
          onLobbyBackClick();
          break;

        // Redirect to live game
        case 'pp_redirect_game': {
          const tableType = message.launchGameType ?? message.tableType;

          if (!tableType || !message.urlMap) return;

          gameRedirect({
            tableType: message.launchGameType ?? message.tableType,
            urlMap: message.urlMap,
          });

          break;
        }

        // Redirect to slot game
        case 'pp_slots_loadGame': {
          if (!message.path) return;

          window.location.assign(message.path);
          break;
        }

        // Expand or minimize lobby
        case 'toggleLobbyIframe':
          if (iframeWrapperRef.current) {
            iframeWrapperRef.current.style.width = `${!message.visible ? 100 : iframeWrapperWidth}%`; // We use inverse value of `message.visible` because of API inconsistency
          }

          setIsLobbyFullscreen(!message.visible);
          break;

        default:
          break;
      }
    };
    window.addEventListener('message', handleMessageFromIframe);

    return () => {
      window.removeEventListener('message', handleMessageFromIframe);
    };
  }, [dispatch, gameRedirect, iframeWrapperWidth, isLobbyFullscreen, tableId]);

  // iOS 17.1 fix: simulate user interaction to force viewport recalculation after rotation (LCE-63290).
  const handleOrientationChange = useCallback(() => {
    const isPortrait = detectPortrait();

    if (isPortrait) {
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    }
  }, []);

  useEffect(() => {
    if (isCurrentBrowserIOS) {
      window.addEventListener('orientationchange', handleOrientationChange);

      return () => {
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }
  }, [handleOrientationChange]);

  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      CoreLobbyMessageController.targets.lobby.setOrigin('*');
      CoreLobbyMessageController.targets.lobby.setWindow(iframeRef.current.contentWindow);
    }
    return () => {
      CoreLobbyMessageController.targets.lobby.reset();
    };
  }, []);

  const onLobbyBackClick = (): void => {
    if (!isWideScreen) dispatch(headerActions.pushNavRoute(NavPages.HOME));
  };

  const isAndroid = useMemo(() => DeviceInfo.getPlatform().IS_ANDROID_OS, []);

  const mods = useModifiers(styles, 'lobby', {
    isFull: lobbyVersion === '2',
    isAndroid,
  });

  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();

  if (!isLobbyOpen) return null;

  return (
    <div
      className={`${styles.lobby} ${mods}`}
      ref={iframeWrapperRef}
      style={{ '--wrapperWidth': `${fullWidth ?? iframeWrapperWidth}%` } as CSSProperties}
    >
      {isIframeLoaded ? null : (
        <IconButton
          className={styles.lobby__closeButton}
          ghost
          icon={Icons.Close}
          large
          onClick={() => dispatch(headerActions.setMiniLobbyOpen(false))}
        />
      )}

      <iframe
        className={styles.lobby__iframeDocument}
        loading="lazy"
        name="lobby_iframe" // is hardcoded in lobby source code to be able to detect that it's loaded in iframe (https://gitlab.gametechlabs.net/pp-live/appmobile/-/blob/master/src/app/lobby/desktop/lobby-desktop.component.ts#L613) @ 2023-12-13
        ref={iframeRef}
        src={LOBBY_URL}
        style={{ '--iframeWidth': `${fullWidth ?? iframeWidth}vw` } as CSSProperties} // TODO maybe a better way to handle this
        title="Lobby"
      />
    </div>
  );
};

export const MiniLobbyConnected = (): ReactElement | null => {
  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();
  const dispatch = useCoreDispatch();

  useEffect(() => {
    if (!isLobbyOpen) {
      dispatch(gameStateActions.setGameSuspended(false));
    }
  }, [isLobbyOpen, dispatch]);

  if (!isLobbyOpen) return null;

  return <MiniLobbyBodyConnected />;
};
